import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store';
import { LogoMark } from './Logo';
import { shouldLift } from '../logo-takeoff';

/*
 * LOGO TAKEOFF — easter egg #2, and the home control it rides on.
 *
 * The sidebar brand mark is the "go to the Board" control — the standard
 * top-left-logo-goes-home affordance. On a click that ACTUALLY travels home
 * (you were on another screen), the mark briefly lifts off: a short climb with a
 * slight nose-up tilt and a thin teal vapour trail, then it settles back into
 * its tile. A happy accident on an action people already do — sibling to
 * "Wheels up" (easter egg #1).
 *
 * It never delays navigation: the view switches at once and the whole flourish
 * is a pointer-events:none overlay. It fires ONLY on a travelling click — never
 * when already on the Board — and is one-shot (a lift already in flight is not
 * re-triggered, so a rapid double-click can't stack). No toast; the reward is
 * the motion itself. prefers-reduced-motion → no lift, the click just navigates.
 *
 * The lift plays in a FIXED overlay portalled to <body>, measured from the
 * mark's own on-screen box, so it escapes `.brand { overflow: hidden }` — the
 * version-chip guard — without weakening it. GPU transform/opacity only, on the
 * app's own --curve-out. Desktop only.
 */

const LIFT_MS = 900;

export default function BrandHome() {
  const view = useApp((s) => s.view);
  const setView = useApp((s) => s.setView);
  const markRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // The lifting copy's on-screen box, or null when the mark is at rest.
  const [flyer, setFlyer] = useState<{ x: number; y: number; s: number } | null>(null);

  const goHome = useCallback(() => {
    setView('board'); // navigation first, always — the flourish never gates it
    const reduced =
      typeof window !== 'undefined' &&
      !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!shouldLift(view, !!flyer, reduced)) return; // already home, in flight, or reduced
    const el = markRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setFlyer({ x: r.left, y: r.top, s: r.width });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFlyer(null), LIFT_MS);
  }, [view, setView, flyer]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return (
    <>
      <button type="button" className="brand-home" aria-label="Go to the Board" onClick={goHome}>
        <span
          ref={markRef}
          className="brand-mark"
          style={{ width: 26, height: 26, visibility: flyer ? 'hidden' : undefined }}
        >
          <LogoMark size={16} />
        </span>
      </button>
      <span className="brand-word">FlightDeck</span>
      {flyer &&
        createPortal(
          <span
            className="lt-flyer"
            aria-hidden="true"
            style={{ left: flyer.x, top: flyer.y, width: flyer.s, height: flyer.s }}
          >
            <span className="lt-tile">
              <LogoMark size={16} />
            </span>
            <span className="lt-trail" />
          </span>,
          document.body
        )}
    </>
  );
}
